import { CalendarClock, Car, Phone } from "lucide-react";
import { STATUS_LABEL, STATUS_TONE, STEPS, money, when } from "@/lib/rides";
import { WaitCharges } from "@/components/driver/PickupTimer";

export const StatusBadge = ({ status }) => (
  <span data-testid="ride-status-badge" className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${STATUS_TONE[status]}`}>
    {STATUS_LABEL[status]}
  </span>
);

export function StepTrack({ status }) {
  const idx = STEPS.indexOf(status);
  return (
    <div className="flex gap-1.5" data-testid="ride-step-track">
      {STEPS.map((s, i) => (
        <span key={s} className={`h-1.5 flex-1 rounded-full ${i <= idx ? "bg-primary shadow-[0_0_8px_hsl(var(--primary)/0.7)]" : "bg-muted"} ${i === idx && status !== "completed" ? "animate-pulse" : ""}`} />
      ))}
    </div>
  );
}

export const Stops = ({ ride }) => {
  const rows = [
    { key: "pickup", dot: "bg-neon-cyan shadow-[0_0_8px_hsl(var(--neon-cyan))]", label: "Pickup", place: ride.pickup },
    ...(ride.stops || []).map((s, i) => ({ key: `stop-${i}`, dot: "bg-neon-violet", label: `Stop ${i + 1}${s.wait_minutes ? ` · wait ${s.wait_minutes} min` : ""}`, place: s })),
    { key: "dropoff", dot: "bg-neon-pink shadow-[0_0_8px_hsl(var(--neon-pink))]", label: "Drop-off", place: ride.dropoff },
  ];
  return (
    <div className="space-y-3 rounded-2xl border bg-card p-4 text-sm">
      {ride.scheduled_at && (
        <p className="flex items-center gap-1.5 text-xs font-semibold text-neon-cyan" data-testid="ride-scheduled-at">
          <CalendarClock className="h-3.5 w-3.5" /> {when(ride.scheduled_at)}
          {ride.occurrence_count ? <span className="text-muted-foreground">· ride {ride.occurrence_index} of {ride.occurrence_count}</span> : null}
        </p>
      )}
      {rows.map((r) => (
        <div key={r.key} className="flex gap-3">
          <span className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ${r.dot}`} />
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{r.label}</p>
            <p className="truncate font-semibold" data-testid={`ride-${r.key}-name`}>{r.place.name}</p>
            {r.place.unit_number && <p className="truncate text-xs font-semibold text-neon-cyan" data-testid={`ride-${r.key}-unit`}>Unit / gate: {r.place.unit_number}</p>}
            {r.place.formatted_address && r.place.formatted_address !== r.place.name && (
              <p className="truncate text-xs text-muted-foreground" data-testid={`ride-${r.key}-address`}>{r.place.formatted_address}</p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export function RideSummary({ ride, perspective }) {
  const other = perspective === "rider"
    ? ride.driver_name && { name: ride.driver_name, sub: ride.driver_vehicle, icon: Car }
    : { name: ride.rider_name, sub: ride.rider_phone, icon: Phone };
  return (
    <div className="space-y-4" data-testid="active-ride-card">
      <div className="flex items-center justify-between">
        <StatusBadge status={ride.status} />
        <span className="font-display text-2xl font-bold tracking-tight" data-testid="active-ride-fare">{money(ride.fare)}</span>
      </div>
      <StepTrack status={ride.status} />
      {other && (
        <div className="flex items-center gap-3 rounded-2xl bg-muted p-4">
          <span className="grid h-11 w-11 place-items-center rounded-full bg-neon-violet font-bold text-white shadow-[0_0_14px_hsl(var(--neon-violet)/0.6)]">{other.name[0]}</span>
          <div className="min-w-0">
            <p className="font-semibold" data-testid="active-ride-counterpart-name">{other.name}</p>
            {other.sub && <p className="flex items-center gap-1 truncate text-xs text-muted-foreground"><other.icon className="h-3 w-3" /> {other.sub}</p>}
          </div>
        </div>
      )}
      <Stops ride={ride} />
      {ride.quote?.promotion && <p className="rounded-xl border border-primary/40 bg-primary/10 px-3 py-2 text-xs" data-testid="ride-promotion">PROMO FARE {money(ride.quote.promotion.fare)} · {ride.quote.promotion.location_name}</p>}
      <WaitCharges ride={ride} />
      <p className="text-xs text-muted-foreground">{ride.distance_miles} mi · {ride.quote?.service_window_minutes ?? ride.planned_window_minutes} min service window · {ride.vehicle_class?.toUpperCase()} · booking fee $0.00</p>
      {ride.notes && <p className="rounded-xl bg-muted px-3 py-2 text-sm" data-testid="active-ride-notes">Note: {ride.notes}</p>}
    </div>
  );
}
